import React from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid2';
import InputAdornment from '@mui/material/InputAdornment';
import Slider from '@mui/material/Slider';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import { ClassNameMap } from '@mui/styles';
import { ProjectNumberField } from 'components/Form';
import { FormikHelpers } from 'formik';
import { Trans } from '@lingui/react';
import { BasicDisplayUnits, getPressurePumpUnitLabel, unitLabels } from 'hooks/useDisplayUnits/useDisplayUnits';
import { Form } from 'hooks/useForm';
import {
  BackfillRecipeForm,
  CustomUnitFields,
  MetricPressureUnit,
  PumpType,
  UcsCoefficientsDto,
  UnitSystem
} from 'providers/api';
import {
  isNumber,
  round
} from 'utils';
import useProjectNumberFormatter from 'modules/project/providers/project/useProjectNumberFormatter';
import { pumpPressureBarToGravityMetricHead, pumpPressureBarToKpa, pumpPressureKpaToBar } from 'utils/unitConversions';
import { DictionaryRange, Range } from '../../../MineModel/types';
import UnitSystemNumberField from './UnitSystemNumberField';
import { Model } from '../../../hooks/useModelState';
import { updateRecipeState } from '../../../canvas/Model/updateRecipeState';
import { calculateFrictionForRoute } from '../../../canvas/Model/updateFriction';
import { waterBinderRatioFromConcentrationAndBinder } from '../../../canvas/PCFunctions/PCOtherFunctions';
import { calculateUCSfromWCandDays } from '../../../canvas/PCFunctions/ucsFunctions';
import { deriveBinderFromMassConcentration } from '../../../canvas/PCFunctions/PCPasteFunctions';

interface AdjustedInputsProps {
  values: BackfillRecipeForm;
  limits: DictionaryRange;
  unitSystemPreference: UnitSystem;
  metricPressureUnit?: MetricPressureUnit;
  customUnitFields: CustomUnitFields[];
  targetDaysCoefficients: { [key: string]: UcsCoefficientsDto } | undefined
  hasUcsData: boolean;
  setFieldValue: FormikHelpers<BackfillRecipeForm>['setFieldValue'];
  setFieldTouched: FormikHelpers<BackfillRecipeForm>['setFieldTouched'];
  hasError: Form['helpers']['hasError'];
  getErrorHelpText: Form['helpers']['getErrorHelpText'];
  classes: ClassNameMap;
  pumpType: PumpType;
  model: Model;
}

const decimalToPercentage = (value: number) => round(value * 100);
const convertTextByPumpType = (pumpType: PumpType) => (
  pumpType === PumpType.Pump
    ? <Trans id="Adjusted Pump Pressure">Adjusted Pump Pressure</Trans>
    : <Trans id="Adjusted Gravity">Adjusted Gravity</Trans>
);

interface UpdateRouteDataParams {
  massConcentration: number | undefined;
  binderContent: number | undefined;
  fromMassConcentration: boolean;
  autoBinder: boolean;
  hasUcsData: boolean;
  binderRange: Range;
  model: Model;
  setFieldValue: FormikHelpers<BackfillRecipeForm>['setFieldValue'];
  targetDaysCoefficients: { [key: string]: UcsCoefficientsDto } | undefined;
  targetDays: number | undefined;
  currentValues: BackfillRecipeForm;
}

const updateRouteData = ({
  massConcentration,
  binderContent,
  fromMassConcentration,
  autoBinder,
  hasUcsData,
  binderRange,
  model,
  setFieldValue,
  targetDaysCoefficients,
  targetDays,
  currentValues,
}: UpdateRouteDataParams): boolean => {
  const finalMassConc = isNumber(massConcentration)
    ? massConcentration
    : currentValues.pasteSelectedSpecification?.massConcentration;
  const carriedBinderConc = isNumber(binderContent)
    ? binderContent
    : currentValues.pasteSelectedSpecification?.binderContent ?? currentValues.pasteOptimisedSpecification?.binderContent;

  if (!isNumber(finalMassConc) || !isNumber(carriedBinderConc) || !model.recipeState) return false;

  const targetUCS = currentValues.pasteSpecification?.targetUCSStrength ?? 0;
  const ucsCoefficients = targetDaysCoefficients && targetDays ? targetDaysCoefficients[targetDays] : undefined;
  const shouldAutoAdjustBinder = autoBinder && fromMassConcentration && hasUcsData && targetUCS > 0 && !!ucsCoefficients;
  const derivedBinder = shouldAutoAdjustBinder
    ? deriveBinderFromMassConcentration(finalMassConc / 100, targetUCS, binderRange, ucsCoefficients)
    : undefined;
  const finalBinderConc = derivedBinder ? round(derivedBinder.binderConcentrationByMass * 100, 2) : carriedBinderConc;

  setFieldValue('pasteSelectedSpecification.massConcentration', finalMassConc);
  setFieldValue('pasteSelectedSpecification.binderContent', finalBinderConc);

  const newRecipeState = updateRecipeState(model, model.recipeState, finalMassConc / 100, finalBinderConc / 100);

  model.setRecipeState(newRecipeState);

  const updatedRoute = calculateFrictionForRoute(model, newRecipeState);

  if (isNumber(updatedRoute?.pumpPressureKpa)) {
    const pumpPressureBar = pumpPressureKpaToBar(updatedRoute.pumpPressureKpa);
    setFieldValue('pasteSelectedSpecification.pumpPressure', pumpPressureBar);
  }

  if (targetDaysCoefficients && targetDays) {
    const coefficients = targetDaysCoefficients[targetDays];
    if (coefficients) {
      const waterBinderRatio = waterBinderRatioFromConcentrationAndBinder(finalMassConc / 100, finalBinderConc / 100);
      const calculatedUCS = calculateUCSfromWCandDays(waterBinderRatio, coefficients.coefficient1, coefficients.coefficient2);
      const ucsStrength = parseInt(calculatedUCS.toPrecision(calculatedUCS > 9999 ? 5 : 4), 10);
      setFieldValue('pasteSelectedSpecification.ucsStrength', ucsStrength);
    }
  }

  model.setSelectedRoute(updatedRoute);

  return derivedBinder?.clamped ?? false;
};

const PasteAdjustedInputs = ({
  values,
  limits,
  unitSystemPreference,
  metricPressureUnit,
  targetDaysCoefficients,
  hasUcsData,
  customUnitFields,
  setFieldValue,
  setFieldTouched,
  hasError,
  getErrorHelpText,
  classes,
  pumpType,
  model,
}: AdjustedInputsProps) => {
  const formatNumber = useProjectNumberFormatter();
  const [autoBinder, setAutoBinder] = React.useState<boolean>(true);
  const [binderClamped, setBinderClamped] = React.useState<boolean>(false);
  const isKiloPascal = pumpType === PumpType.Pump
    && metricPressureUnit === MetricPressureUnit.KiloPascal
    && unitSystemPreference === UnitSystem.Metric;
  const targetDaysCoefficient = values.pasteSpecification?.targetDays ? targetDaysCoefficients?.[values.pasteSpecification.targetDays] : undefined;
  const canAutoAdjustBinder = hasUcsData && (values.pasteSpecification?.targetUCSStrength ?? 0) > 0 && !!targetDaysCoefficient;
  const isBinderAutoAdjusted = autoBinder && canAutoAdjustBinder;
  const selectedPumpPressureBar = values.pasteSelectedSpecification?.pumpPressure;

  const applyRouteData = (
    massConcentration: number | undefined,
    binderContent: number | undefined,
    fromMassConcentration: boolean,
  ) => setBinderClamped(updateRouteData({
    massConcentration,
    binderContent,
    fromMassConcentration,
    autoBinder,
    hasUcsData,
    binderRange: limits.cement,
    model,
    setFieldValue,
    targetDaysCoefficients,
    targetDays: values.pasteSpecification?.targetDays,
    currentValues: values,
  }));

  let displayAdjustedPumpOrGravity = selectedPumpPressureBar;
  if (pumpType !== PumpType.Pump && isNumber(selectedPumpPressureBar)) {
    displayAdjustedPumpOrGravity = pumpPressureBarToGravityMetricHead(selectedPumpPressureBar);
  } else if (isKiloPascal && isNumber(selectedPumpPressureBar)) {
    displayAdjustedPumpOrGravity = pumpPressureBarToKpa(selectedPumpPressureBar);
  }

  return (
    <Grid container spacing={1}>
      <Grid size={12}>
        <Divider className={classes.fullWidthDivider} />
      </Grid>

      {/* Actual Pump Pressure */}
      <Grid size={12}>
        <FormHelperText id="pump-pressure-helper-text" component="label">{convertTextByPumpType(pumpType)}</FormHelperText>
        <UnitSystemNumberField
          unitSystem={unitSystemPreference}
          customUnitFields={customUnitFields}
          unitInfo={pumpType === PumpType.Pump ? getPressurePumpUnitLabel(metricPressureUnit ?? MetricPressureUnit.Bar) : unitLabels[BasicDisplayUnits.Gravity]}
          decimalPlaces={isKiloPascal ? 0 : 1}
          disabled
          error={false}
          helperText={false}
          size="small"
          id={pumpType === PumpType.Pump ? 'AdjustedPumpPressure' : 'AdjustedGravity'}
          name={pumpType === PumpType.Pump ? 'AdjustedPumpPressure' : 'AdjustedGravity'}
          aria-labelledby="pump-pressure-helper-text"
          value={displayAdjustedPumpOrGravity}
        />
      </Grid>

      {/* Adjusted UCS Strength */}
      {hasUcsData && (
        <>
          <Grid size={7}>
            <FormHelperText id="adjusted-ucs-strength-helper-text" component="label"><Trans id="Adjusted UCS Strength">Adjusted UCS Strength</Trans></FormHelperText>
            <UnitSystemNumberField
              unitSystem={unitSystemPreference}
              customUnitFields={customUnitFields}
              unitInfo={unitLabels[BasicDisplayUnits.PressureUCS]}
              decimalPlaces={2}
              disabled
              error={false}
              helperText={false}
              size="small"
              id="AdjustedUCSStrength"
              name="AdjustedUCSStrength"
              aria-labelledby="adjusted-ucs-strength-helper-text"
              value={values.pasteSelectedSpecification?.ucsStrength}
            />
          </Grid>
          <Grid size={1}>
            <div className={classes.at}>
              <Trans id="at">at</Trans>
            </div>
          </Grid>
          <Grid size={4}>
            <FormHelperText id="adjusted-days-helper-text" component="label"><Trans id="Days">Days</Trans></FormHelperText>
            <TextField
              disabled
              size="small"
              type="number"
              error={false}
              helperText={false}
              fullWidth
              id="Adjusted Days"
              name="Adjusted Days"
              aria-labelledby="adjusted-days-helper-text"
              value={values.pasteSpecification?.targetDays ?? Object.keys(targetDaysCoefficients ?? {}).sort((a, b) => parseFloat(a) - parseFloat(b))[0]}
            />
          </Grid>
        </>
      )}

      {/* Concentration: Optimal vs. Adjusted */}
      <Grid size={6}>
        <FormHelperText id="optimal-concentration-helper-text" component="label"><Trans id="Optimal Concentration">Optimal Concentration</Trans></FormHelperText>
        <TextField
          size="small"
          type="text"
          disabled
          fullWidth
          id="optimal-concentration-input"
          name="optimal-concentration-input"
          aria-labelledby="optimal-concentration-helper-text"
          value={formatNumber(values.pasteOptimisedSpecification?.massConcentration ?? 0, 2)}
          slotProps={{
            input: {
              endAdornment: <InputAdornment position="end">%m</InputAdornment>,
            },
          }}
        />
      </Grid>

      <Grid size={6}>
        <FormHelperText id="adjusted-concentration-helper-text" component="label"><Trans id="Adjusted Concentration">Adjusted Concentration</Trans></FormHelperText>
        <ProjectNumberField
          size="small"
          fullWidth
          id="adjusted-concentration-input"
          name="adjusted-concentration-input"
          decimalPlaces={2}
          value={values.pasteSelectedSpecification?.massConcentration ?? 0}
          onChange={(parsed) => applyRouteData(parsed, values.pasteSelectedSpecification?.binderContent, true)}
          aria-labelledby="adjusted-concentration-helper-text"
          onBlur={() => {
            setFieldValue('pasteSelectedSpecification.massConcentration', Number(values.pasteSelectedSpecification?.massConcentration?.toFixed(2)));
            setFieldTouched('pasteSelectedSpecification.massConcentration', true, false);
          }}
          error={hasError('pasteSelectedSpecification.massConcentration')}
          helperText={getErrorHelpText('pasteSelectedSpecification.massConcentration')}
          slotProps={{
            input: {
              endAdornment: <InputAdornment position="end">%m</InputAdornment>,
            },
          }}
        />
      </Grid>

      <Grid size={6} />

      <Grid size={6}>
        <Box
          sx={{
            mx: 1,
          }}
        >
          <Slider
            color="secondary"
            value={values.pasteSelectedSpecification?.massConcentration}
            min={decimalToPercentage(limits.concentration.min)}
            step={round(limits.concentration.interval * 100)}
            max={decimalToPercentage(limits.concentration.max)}
            onChange={(e, n) => applyRouteData(n as number, values.pasteSelectedSpecification?.binderContent, true)}
            aria-labelledby="adjusted-concentration-helper-text"
          />
        </Box>
      </Grid>

      {/* Cement: Optimal vs. Adjusted */}
      {hasUcsData && (
        <>
          {canAutoAdjustBinder && (
            <Grid size={12}>
              <FormControlLabel
                control={(
                  <Switch
                    color="secondary"
                    size="small"
                    checked={autoBinder}
                    onChange={(event) => setAutoBinder(event.target.checked)}
                    inputProps={{ 'aria-label': 'auto-adjust-binder' }}
                  />
                )}
                label={<Trans id="Auto-adjust binder">Auto-adjust binder</Trans>}
              />
              {isBinderAutoAdjusted && binderClamped && (
                <FormHelperText error>
                  <Trans id="Binder clamped to the allowed range, the adjusted UCS will not match the target">
                    Binder clamped to the allowed range, the adjusted UCS will not match the target
                  </Trans>
                </FormHelperText>
              )}
            </Grid>
          )}

          <Grid size={6}>
            <FormHelperText id="optimal-cement-helper-text"><Trans id="Optimal Binder">Optimal Binder</Trans></FormHelperText>
            <TextField
              size="small"
              type="text"
              disabled
              fullWidth
              id="optimal-strength-input"
              name="optimal-strength-input"
              value={formatNumber(values.pasteOptimisedSpecification?.binderContent ?? 0, 1)}
              aria-labelledby="optimal-cement-helper-text"
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                },
              }}
            />
          </Grid>

          <Grid size={6}>
            <FormHelperText id="adjusted-cement-helper-text"><Trans id="Adjusted Binder">Adjusted Binder</Trans></FormHelperText>
            <ProjectNumberField
              size="small"
              fullWidth
              disabled={isBinderAutoAdjusted}
              id="adjusted-cement-input"
              name="adjusted-cement-input"
              decimalPlaces={1}
              value={values.pasteSelectedSpecification?.binderContent ?? 0}
              onChange={(parsed) => applyRouteData(values.pasteSelectedSpecification?.massConcentration, parsed, false)}
              aria-labelledby="adjusted-cement-helper-text"
              onBlur={() => setFieldTouched('pasteSelectedSpecification.binderContent', true, true)}
              error={hasError('pasteSelectedSpecification.binderContent')}
              helperText={getErrorHelpText('pasteSelectedSpecification.binderContent')}
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                },
              }}
            />
          </Grid>

          <Grid size={6}>
            <FormControl variant="standard" />
          </Grid>

          <Grid size={6}>
            <Box sx={{
              mx: 1,
            }}
            >
              <Slider
                color="secondary"
                disabled={isBinderAutoAdjusted}
                value={values.pasteSelectedSpecification?.binderContent}
                min={decimalToPercentage(limits.cement.min)}
                max={decimalToPercentage(limits.cement.max)}
                step={round(limits.cement.interval * 100)}
                onChange={(e, n) => applyRouteData(values.pasteSelectedSpecification?.massConcentration, n as number, false)}
                aria-labelledby="adjusted-cement-helper-text"
              />
            </Box>
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default PasteAdjustedInputs;
